import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, CheckCircle2, RefreshCcw, UserCheck, UserMinus, Users, Wallet } from 'lucide-react';
import Layout from '../components/Layout';
import Notification from '../components/Notification';
import Button from '../components/elements/Button';
import Loading from '../components/elements/Loading';
import { fetchDashboard } from '../utils/adminDashboard';

const dashboardSections = [
  {
    id: 'users',
    title: 'User Health',
    description: 'Account activity and lifecycle overview',
    gridCols: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
    badgeClass: 'bg-sky-100 text-sky-700',
    cardClass: 'border-sky-200 bg-gradient-to-br from-sky-500/10 to-transparent',
    iconClass: 'text-sky-600',
    metrics: [
      { key: 'users', title: 'Total Users', icon: Users },
      { key: 'activeUsers', title: 'Active Users', icon: UserCheck },
      { key: 'deletedUsers', title: 'Deleted Users', icon: UserMinus },
      { key: 'nomineeAccessGranted', title: 'Nominee Access', icon: Activity },
    ],
  },
  {
    id: 'subscriptions',
    title: 'Subscription Mix',
    description: 'Plan distribution and verification status',
    gridCols: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    badgeClass: 'bg-violet-100 text-violet-700',
    cardClass: 'border-violet-200 bg-gradient-to-br from-violet-500/10 to-transparent',
    iconClass: 'text-violet-600',
    metrics: [
      { key: 'subscriptions', title: 'With Subscription', icon: CheckCircle2 },
      { key: 'verifiedSubscriptions', title: 'Verified Subscriptions', icon: CheckCircle2 },
      { key: 'freeSubscriptions', title: 'Free Subscriptions', icon: Wallet },
      { key: 'silverSubscriptions', title: 'Silver Subscriptions', icon: Wallet },
      { key: 'goldSubscriptions', title: 'Gold Subscriptions', icon: Wallet },
      { key: 'platinumSubscriptions', title: 'Platinum Subscriptions', icon: Wallet },
    ],
  },
  {
    id: 'kpis',
    title: 'Derived KPIs',
    description: 'Conversion and health ratios derived from core metrics',
    gridCols: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5',
    badgeClass: 'bg-amber-100 text-amber-700',
    cardClass: 'border-amber-200 bg-gradient-to-br from-amber-500/10 to-transparent',
    iconClass: 'text-amber-600',
    metrics: [
      { key: 'activeUserRatio', title: 'Active User Ratio', icon: Activity, isPercent: true },
      { key: 'subscriptionPenetration', title: 'Subscription Penetration', icon: CheckCircle2, isPercent: true },
      { key: 'verificationRate', title: 'Verification Rate', icon: CheckCircle2, isPercent: true },
      { key: 'referredUserRate', title: 'Referral Conversion', icon: Users, isPercent: true },
      { key: 'referredVerifiedRate', title: 'Referred Quality', icon: UserCheck, isPercent: true },
      { key: 'nomineeAccessCoverage', title: 'Nominee Coverage', icon: UserCheck, isPercent: true },
      { key: 'deletedUserRatio', title: 'Deleted User Ratio', icon: UserMinus, isPercent: true },
      { key: 'paidPlanMix', title: 'Paid Plan Mix', icon: Wallet, isPercent: true },
      { key: 'referralPayoutEfficiency', title: 'Referral Payout Efficiency', icon: Wallet, isPercent: true },
      { key: 'referralPayoutBacklog', title: 'Payout Backlog', icon: Wallet, isCurrency: true },
    ],
  },
  {
    id: 'referrals',
    title: 'Referral Economy',
    description: 'Referral participation and payout totals',
    gridCols: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    badgeClass: 'bg-emerald-100 text-emerald-700',
    cardClass: 'border-emerald-200 bg-gradient-to-br from-emerald-500/10 to-transparent',
    iconClass: 'text-emerald-600',
    metrics: [
      { key: 'referredUsers', title: 'Referred Users', icon: Users },
      { key: 'referralAmountTotal', title: 'Referral Amount Total', icon: Wallet, isCurrency: true },
      { key: 'referralAmountClaimed', title: 'Referral Claimed', icon: Wallet, isCurrency: true },
      { key: 'referralAmountUnclaimed', title: 'Referral Unclaimed', icon: Wallet, isCurrency: true },
      { key: 'referralAmountProcessing', title: 'Referral Processing', icon: Wallet, isCurrency: true },
    ],
  },
];

const formatValue = (value, isCurrency) => {
  if (typeof value !== 'number') {
    value = Number(value || 0);
  }

  if (isCurrency) {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(value);
  }

  return new Intl.NumberFormat('en-IN').format(value);
};

const formatPercent = (value) => {
  const normalized = Number(value || 0);
  return `${normalized.toFixed(1)}%`;
};

const renderMetricValue = (dashboard, card) => {
  const value = dashboard[card.key];
  if (card.isPercent) {
    return formatPercent(value);
  }

  return formatValue(value, card.isCurrency);
};

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboard, setDashboard] = useState({});
  const [notification, setNotification] = useState({ message: '', type: 'success' });

  const isLoggedIn = sessionStorage.getItem('isLoggedIn') === 'true';
  const role = sessionStorage.getItem('role');

  const getMetricLink = (metricKey) => {
    const usersMetrics = new Set([
      'users',
      'activeUsers',
      'deletedUsers',
      'nomineeAccessGranted',
      'subscriptions',
      'verifiedSubscriptions',
      'freeSubscriptions',
      'silverSubscriptions',
      'goldSubscriptions',
      'platinumSubscriptions',
      'paidSubscriptions',
      'referredUsers',
      'referredVerifiedUsers',
      'activeUserRatio',
      'subscriptionPenetration',
      'verificationRate',
      'referredUserRate',
      'referredVerifiedRate',
      'nomineeAccessCoverage',
      'deletedUserRatio',
      'paidPlanMix',
      'referralPayoutEfficiency',
      'referralPayoutBacklog',
      'referralAmountTotal',
      'referralAmountClaimed',
      'referralAmountUnclaimed',
      'referralAmountProcessing',
    ]);

    if (usersMetrics.has(metricKey)) {
      return `/admin-users?metric=${encodeURIComponent(metricKey)}`;
    }

    return null;
  };

  const loadDashboard = async (isManualRefresh = false) => {
    if (isManualRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await fetchDashboard();
      if (!response?.success) {
        setNotification({ message: response?.message || 'Failed to load dashboard.', type: 'error' });
        return;
      }
      setDashboard(response.data || {});
    } catch (_error) {
      setNotification({ message: 'Failed to load dashboard.', type: 'error' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    document.title = 'Admin Dashboard | Eternal Locker™';

    if (!isLoggedIn) {
      navigate('/');
      return;
    }

    if (role !== 'admin') {
      navigate('/dashboard');
      return;
    }

    loadDashboard();
  }, []);

  return (
    <Layout>
      {notification.message && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification({ message: '', type: 'success' })}
        />
      )}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-primary-dark">Admin Dashboard</h1>
          <p className="text-sm text-primary-dark mt-1">System-level usage and referral analytics overview.</p>
        </div>

        <Button
          type="secondary"
          onClick={() => loadDashboard(true)}
          disabled={loading || refreshing}
        >
          <RefreshCcw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="w-full py-16 flex items-center justify-center">
          <Loading />
        </div>
      ) : (
        <div className="space-y-8">
          {dashboardSections.map((section) => (
            <section key={section.id}>
              <div className="flex items-center justify-between gap-3 mb-3">
                <div>
                  <h2 className="text-xl font-bold text-primary-dark">{section.title}</h2>
                  <p className="text-sm text-primary-dark/80">{section.description}</p>
                </div>
                <span className={`text-xs font-semibold px-3 py-1 rounded-full ${section.badgeClass}`}>
                  {section.metrics.length} metrics
                </span>
              </div>

              <div className={`grid gap-4 ${section.gridCols}`}>
                {section.metrics.map((card) => {
                  const Icon = card.icon;
                  const metricLink = getMetricLink(card.key);
                  return (
                    <button
                      type="button"
                      key={card.key}
                      onClick={() => metricLink && navigate(metricLink)}
                      disabled={!metricLink}
                      className={`bg-primary-light border rounded-xl p-4 shadow-sm text-left transition hover:shadow-md hover:-translate-y-0.5 ${metricLink ? 'cursor-pointer' : 'cursor-default'} ${section.cardClass}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-semibold uppercase tracking-wide text-primary-dark/80">
                          {card.title}
                        </span>
                        <Icon className={`w-4 h-4 ${section.iconClass}`} />
                      </div>
                      <div className="text-2xl font-bold text-primary-dark break-words">
                        {renderMetricValue(dashboard, card)}
                      </div>
                      {metricLink && (
                        <div className="mt-2 text-xs text-primary-dark/70 text-left">View filtered users</div>
                      )}
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </Layout>
  );
}
